import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoaderService } from '@shared';
import { jwtDecode } from 'jwt-decode';
import { isTokenExpired } from '../utils';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { RoleCodes } from '@auth';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  public tokenEndpoint = environment.tokenUrl;
  //groupTypeId?: number;
  token: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private conceptMapping : Map<string, string> = new Map([
    ['11', 'BK_CA'], 
    ['1', 'BK_US'], 
    ['-1', 'BK_US']
  ]);

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private injector: Injector,
    private router: Router,
  ) {}

  getToken(): Observable<boolean> {
    return this.requestToken('token');
  }

  private requestToken(tokenUrl: string) {
    this.loaderService.show();
    return new Observable<boolean>((observer) => {
      this.http
        .get<any>(this.tokenEndpoint + '/' + tokenUrl, {
          withCredentials: true,
        })
        .subscribe({
          next: (response) => {
            if (response.access_token) {
              this.postTokenLoadedToParent();
              this.token.next(response.access_token);
              this.loaderService.hide();
              observer.next(true);
              observer.complete();
            } else {
              this.postUnauthorizedToParent();
              this.loaderService.hide();
              observer.next(false);
              observer.error('Access token not found');
            }
          },
          error: (error) => {
            this.postUnauthorizedToParent();
            this.loaderService.hide();
            observer.next(false);
            observer.error(error);
          },
        });
    });
  }

  private postUnauthorizedToParent() {
    window.parent.postMessage({ type: 'UNAUTHORIZED', data: '' }, '*');
  }

  private postTokenLoadedToParent() {
    window.parent.postMessage({ type: 'APP_LOADED' }, '*');
  }

  refreshToken(): Observable<any> {
    return this.requestToken('refreshtoken');
  }

  private inFlightToken$?: Observable<string>;

  private obtainValidToken$(): Observable<string> {
    const t = this.token.value;

    // Fast path: existing, non-expired token
    if (t && !isTokenExpired(t)) return of(t);

    // If a fetch/refresh is already running, reuse it
    if (this.inFlightToken$) return this.inFlightToken$;

    // Decide whether to fetch or refresh
    const src$ = t ? this.refreshToken() : this.getToken();

    this.inFlightToken$ = src$.pipe(
      switchMap((ok) => {
        const newToken = this.token.value;
        if (ok && newToken) return of(newToken);
        return throwError(() => new Error('No token available'));
      }),
      // Cache result for concurrent callers; cleared after first completion
      shareReplay(1),
      finalize(() => (this.inFlightToken$ = undefined)),
    );

    return this.inFlightToken$;
  }

  async getTokenAttribute<T = any>(attribute: string): Promise<T | 'unknown'> {
    const tok = await firstValueFrom(this.obtainValidToken$()).catch(
      () => undefined,
    );
    if (!tok) return 'unknown';
    try {
      const decoded: any = jwtDecode(tok);
      return (decoded?.[attribute] ?? 'unknown') as T | 'unknown';
    } catch {
      return 'unknown';
    }
  }

  async getRoleCodes(): Promise<string[]> {
    const roleCodesClaim = await this.getTokenAttribute<string>('RoleCodes');

    if (!roleCodesClaim || roleCodesClaim === 'unknown') {
      return [];
    }

    try {
      // Parse comma-separated role codes and filter out empty values
      const roleCodes = roleCodesClaim
        .split(',')
        .map((code) => code.trim())
        .filter((code) => code.length > 0);

      return roleCodes;
    } catch {
      return [];
    }
  }

  async isAvailabilityOnly(): Promise<boolean> {
    const roleCodes = await this.getRoleCodes();
    return roleCodes.includes(RoleCodes.AvailabilityOnly);
  }

  async isAdmin(): Promise<boolean> {
    const roleCodes = await this.getRoleCodes();
    return roleCodes.includes(RoleCodes.Admin);
  }

  async currentConceptKey() : Promise<string | undefined> {
    const groupTypeId = await this.getTokenAttribute('GroupTypeId');
    return this.conceptMapping.get(groupTypeId);
  }

  checkAccessToRoute(routePath: string) {
    const routeConfig: any = this.router.config[0].children?.find(
      (x) => x.path === routePath,
    );

    if (routeConfig && routeConfig.canActivate) {
      const guard = routeConfig.canActivate[0];
      const guardInstance = this.injector.get(guard);
      // Build a mock ActivatedRouteSnapshot with routeConfig.data
      const routeSnapshot = {
        data: routeConfig.data || {},
        // Optionally, add more properties if your guard uses them
      } as ActivatedRouteSnapshot;

      const stateSnapshot = {} as RouterStateSnapshot;
      const value = guardInstance.canActivate(routeSnapshot, stateSnapshot);
      return value;
    }
  }
}
